class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // KROSPER's header delegates pointer activation and survives section reloads.
    if (this._krKeyHandler) return;
    this._krKeyHandler = (event) => {
      const link = event.target.closest?.('#cart-icon-bubble');
      if (link && event.code === 'Space') { event.preventDefault(); this.open(link); }
    };
    document.addEventListener('keydown', this._krKeyHandler);
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    this._krPageShow = (event) => { if (event.persisted) this.querySelector('cart-drawer-items')?.onCartUpdate(); };
    window.addEventListener('pageshow', this._krPageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._krPageShow);
    document.removeEventListener('keydown', this._krKeyHandler);
    this._krKeyHandler = null;
    if (this.classList.contains('active')) this.close();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartLink = document.getElementById('cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', 'true');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this._krOpenTimer);
    this._krOpenTimer = setTimeout(() => {
      if (!this.isConnected) return;
      this.classList.add('animate', 'active');
    });

    clearTimeout(this._krFocusTimer);
    this._krFocusTimer = setTimeout(() => {
      if (!this.classList.contains('active')) return;
      const container = this.querySelector('#CartDrawer');
      const focus = this.querySelector('.drawer__inner');
      if (container && focus) trapFocus(container, focus);
    }, 40);

    clearTimeout(this._krFocusRetry);
    this._krFocusRetry = setTimeout(() => {
      if (this.classList.contains('active') && !this.contains(document.activeElement)) {
        const container = this.querySelector('#CartDrawer');
        const focus = this.querySelector('.drawer__inner');
        if (container && focus) trapFocus(container, focus);
      }
    }, 400);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this._krFocusRetry);
    clearTimeout(this._krOpenTimer);
    clearTimeout(this._krFocusTimer);
    this.classList.remove('active');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const updates = this.getSectionsToRender().map((section) => {
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      const source = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section') : null;
      return { target, source };
    });
    // Section rendering can return null with HTTP 200. The item is already added.
    if (updates.some(({ target, source }) => target && !source)) {
      window.location.assign(window.routes.cart_url);
      return;
    }
    this.productId = parsedState.id;
    updates.forEach(({ target, source }) => { if (target && source) target.innerHTML = source.innerHTML; });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
